import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { LocalTaxMode, TaxesDTO, TaxEstimationMode } from '../../api/useTaxesQuery';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { InputNumberMask } from '../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Radio } from '../../../../CommonComponents/OPL/Radio/Radio';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { FormValues, NEW_YORK_STATE } from '../../Taxes.meta';

interface Props {
  isPostRetirement?: boolean;
  data: TaxesDTO;
}

export const TaxesForm = ({ isPostRetirement, data }: Props) => {
  const formBag = useFormContext<FormValues>();

  const [
    preRetirementTaxEstimationMode,
    postRetirementTaxEstimationMode,
    preRetirementLocalTaxMode,
    postRetirementLocalTaxMode,
    postRetirementSelectedFilingState,
  ] = formBag.watch([
    'preRetirementTaxEstimationMode',
    'postRetirementTaxEstimationMode',
    'preRetirementLocalTaxMode',
    'postRetirementLocalTaxMode',
    'postRetirementSelectedFilingState',
  ]);

  const taxEstimationMode = isPostRetirement
    ? postRetirementTaxEstimationMode
    : preRetirementTaxEstimationMode;

  const localTaxMode = isPostRetirement ? postRetirementLocalTaxMode : preRetirementLocalTaxMode;

  const localTaxModeOptions = isPostRetirement
    ? data.postRetirementLocalTaxOptions
    : data.localTaxModeOptions;

  const filteredLocalTaxModeOptions =
    postRetirementSelectedFilingState === NEW_YORK_STATE
      ? localTaxModeOptions
      : localTaxModeOptions.filter(({ value }) => value !== LocalTaxMode.NewYorkCityBrackets);

  const stateOptions = [
    {
      label: 'United States',
      options: data.filingStateOptions.filter(({ IsInsideUS }) => IsInsideUS),
    },
    {
      label: 'Outside U.S.',
      options: data.filingStateOptions.filter(({ IsInsideUS }) => !IsInsideUS),
    },
  ];

  return (
    <Box maxWidth={400}>
      {!data.canUseEffectiveTaxRateOnly && (
        <Controller
          name={
            isPostRetirement ? 'postRetirementTaxEstimationMode' : 'preRetirementTaxEstimationMode'
          }
          control={formBag.control}
          render={({ field: estimationModeField }) => (
            <Box
              role="radiogroup"
              display="flex"
              flexDirection="column"
              aria-labelledby={`tax-estimation-mode--radio-group${
                isPostRetirement ? 'postRetirement' : ''
              }`}
              style={{ gap: 8 }}
            >
              <Text
                id={`tax-estimation-mode--radio-group${isPostRetirement ? 'postRetirement' : ''}`}
                as="p"
                variant="formLabel"
                color="neutral.600"
              >
                Select Tax Rate Method
              </Text>

              <Radio
                name={estimationModeField.name}
                value={TaxEstimationMode.Automatic}
                checked={estimationModeField.value === TaxEstimationMode.Automatic}
                onChange={() => {
                  estimationModeField.onChange(TaxEstimationMode.Automatic);
                }}
              >
                Automatically estimate taxes
              </Radio>

              <Radio
                name={estimationModeField.name}
                value={TaxEstimationMode.UseEffectiveTaxRate}
                checked={estimationModeField.value === TaxEstimationMode.UseEffectiveTaxRate}
                onChange={() => {
                  estimationModeField.onChange(TaxEstimationMode.UseEffectiveTaxRate);
                }}
              >
                Use an effective tax rate
              </Radio>
            </Box>
          )}
        />
      )}

      {taxEstimationMode === TaxEstimationMode.Automatic && (
        <Box display="flex" flexDirection="column" style={{ gap: 8 }} mt="4">
          {isPostRetirement && (
            <Controller
              control={formBag.control}
              name="postRetirementSelectedFilingState"
              render={({ field }) => (
                <Select
                  label="Filing State"
                  value={stateOptions
                    .flatMap(({ options }) => options)
                    .find(({ value }) => value === field.value)}
                  options={stateOptions}
                  onChange={opt => {
                    if (opt) {
                      field.onChange(opt.value);
                      formBag.setValue('postRetirementLocalTaxMode', LocalTaxMode.No);
                    }
                  }}
                />
              )}
            />
          )}

          <Controller
            control={formBag.control}
            name={isPostRetirement ? 'postRetirementLocalTaxMode' : 'preRetirementLocalTaxMode'}
            render={({ field }) => (
              <Select
                label="Local Tax"
                value={filteredLocalTaxModeOptions.find(({ value }) => value === field.value)}
                options={filteredLocalTaxModeOptions}
                onChange={opt => {
                  if (opt) {
                    field.onChange(opt.value);
                  }
                }}
              />
            )}
          />

          <Box display="flex" css="gap: 8px">
            {[LocalTaxMode.PercentageOfIncome, LocalTaxMode.PercentageOfStateTax].includes(
              localTaxMode,
            ) && (
              <Box flex={1} flexShrink={0}>
                <Controller
                  control={formBag.control}
                  name={
                    isPostRetirement
                      ? 'postRetirementPercentageLocalTax'
                      : 'preRetirementPercentageLocalTax'
                  }
                  render={({ field: { onChange, ...field } }) => (
                    <InputNumberMask
                      {...field}
                      label={
                        localTaxMode === LocalTaxMode.PercentageOfIncome
                          ? 'Percentage of Income'
                          : 'Percentage of State Taxes'
                      }
                      allowNegative={false}
                      rightIcon="far fa-percent"
                      onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                    />
                  )}
                />
              </Box>
            )}

            {localTaxMode !== LocalTaxMode.No && (
              <Box flex={1} flexShrink={0}>
                <Controller
                  control={formBag.control}
                  name={
                    isPostRetirement ? 'postRetirementFlatLocalTax' : 'preRetirementFlatLocalTax'
                  }
                  render={({ field: { onChange, ...field } }) => (
                    <InputNumberMask
                      {...field}
                      label={`${
                        localTaxMode !== LocalTaxMode.FlatAmount ? 'Additional ' : ''
                      }Annual Local Taxes`}
                      leftIcon="fa fa-dollar-sign"
                      allowNegative={false}
                      onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                    />
                  )}
                />
              </Box>
            )}
          </Box>
        </Box>
      )}

      {taxEstimationMode === TaxEstimationMode.UseEffectiveTaxRate && (
        <Box pl={28} mt="4">
          <Controller
            control={formBag.control}
            name={
              isPostRetirement ? 'postRetirementEffectiveTaxRate' : 'preRetirementEffectiveTaxRate'
            }
            render={({ field: { onChange, ...field } }) => (
              <InputNumberMask
                {...field}
                aria-label="Effective tax rate"
                allowNegative={false}
                rightIcon="far fa-percent"
                onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
              />
            )}
          />
        </Box>
      )}
    </Box>
  );
};
